import React, { useEffect, useRef, useState, MouseEvent as ReactMouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';
import SideBarSubMenu from '../containers/SideBarSubMenu';
import Icon from '../../../components/icon/Icon';

export interface SideNavItemProps {
  active?: boolean;
  link?: string;
  icon?: string | null;
  label?: string;
  labelWithoutTranslation?: string;
  description?: string;
  sublabel?: string;
  onClick?: () => void;
  tooltip?: string;
  iconSize?: number;
  submenuLevelCheck?: number;
  badge?: number | string | null;
  iconColor?: string;
  submenuIcon?: string;
  submenuLabel?: string;
  submenuLabelWithoutTranslation?: string;
  targetElement?: JSX.Element;
  children?: React.ReactNode;
}

const DEFAULT_ICON = 'keyboard_arrow_right';

const LinkWrapper: React.FC<{
  title?: string | null;
  children: React.ReactNode;
  link?: string;
  onClick?: (e?: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  submenu?: boolean;
  submenuLevelCheck?: number;
  active?: boolean;
}> = ({ title, children, link, onClick, submenu }) => {
  if (link) {
    return (
      <NavLink
        to={link}
        title={title ?? undefined}
        onClick={(event) => {
          if (submenu) event.preventDefault();
          onClick && onClick(event);
        }}
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        {children}
      </NavLink>
    );
  }

  return (
    <a
      title={title ?? undefined}
      href="/"
      onClick={(e) => {
        e.preventDefault();
        onClick && onClick(e);
      }}
    >
      {children}
    </a>
  );
};

const SideNavItem: React.FC<SideNavItemProps> = ({
  icon = DEFAULT_ICON,
  label,
  labelWithoutTranslation,
  submenuLabelWithoutTranslation,
  description,
  badge,
  children,
  tooltip,
  link,
  iconSize,
  submenuIcon,
  submenuLabel,
  targetElement,
  onClick,
  sublabel,
  active: propActive,
  submenuLevelCheck = 1,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const elRef = useRef<HTMLLIElement | null>(null);

  const handleSideNavToggle = () => {
    if (children) {
      setSideNavOpen((prev) => {
        const next = !prev;
        document.body.classList.toggle('side-bar-open', next);
        if (!next) document.body.classList.remove('sidebar-mobile-open');
        return next;
      });
    }
  };

  const handleSideBarClose = () => {
    setSideNavOpen(false);
    document.body.classList.remove('sidebar-mobile-open', 'side-bar-open');
  };

  useEffect(() => {
    handleSideBarClose();
  }, [location.pathname]);

  useEffect(() => {
    const onBodyClick = (event: MouseEvent) => {
      const content = document.body.querySelector('.app-sidebar');
      const target = event.target as Node | null;

      if (
        elRef.current &&
        target &&
        !elRef.current.contains(target) &&
        content &&
        content.contains(target) &&
        sideNavOpen
      ) {
        handleSideBarClose();
      }
    };

    document.body.addEventListener('click', onBodyClick);
    return () => {
      document.body.removeEventListener('click', onBodyClick);
      document.body.classList.remove('side-bar-open');
    };
  }, [sideNavOpen]);

  const computedActive = (() => {
    if (propActive) return true;
    if (!link) return false;

    try {
      const currentParts = location.pathname.split('/');
      const linkParts = link.split('/');
      if (
        children &&
        currentParts[submenuLevelCheck] &&
        linkParts[submenuLevelCheck] &&
        currentParts[submenuLevelCheck] === linkParts[submenuLevelCheck]
      ) {
        return true;
      }
      return location.pathname === link;
    } catch {
      return location.pathname === link;
    }
  })();

  const titleText = tooltip ? intl.formatMessage({ id: tooltip }) : undefined;

  const renderLabel = () => {
    if (label) return intl.formatMessage({ id: label });
    if (labelWithoutTranslation) return labelWithoutTranslation;
    return null;
  };

  const renderDescription = () => (description ? intl.formatMessage({ id: description }) : null);

  const renderSublabel = () => (sublabel ? intl.formatMessage({ id: sublabel }) : null);

  const renderSubmenuLabel = () => {
    if (submenuLabel) return intl.formatMessage({ id: submenuLabel });
    if (submenuLabelWithoutTranslation) return submenuLabelWithoutTranslation;
    return null;
  };

  return (
    <li className={`side-nav-item ${sideNavOpen ? 'open' : ''}`} ref={elRef}>
      <LinkWrapper
        active={computedActive}
        title={titleText ?? null}
        submenu={Boolean(children)}
        submenuLevelCheck={submenuLevelCheck}
        onClick={() => {
          onClick && onClick();
          children ? handleSideNavToggle() : handleSideBarClose();
        }}
        link={link}
      >
        {icon && (
          <div className="notification-badge--wrapper">
            {badge && badge !== 0 && <span className="notification-badge">{badge}</span>}
            <Icon className="navigation-icon" style={{ height: iconSize }} icon={icon} />
          </div>
        )}

        {targetElement}

        <span>
          {renderLabel() && (
            <span className="navigation-text">
              {renderLabel()}
              {/* {computedActive && <i> - {intl.formatMessage({ id: 'selected' })}</i>} */}
            </span>
          )}

          {labelWithoutTranslation && !label && (
            <span className="navigation-text">{labelWithoutTranslation}</span>
          )}

          {renderDescription() && (
            <span className="navigation-description">{renderDescription()}</span>
          )}
        </span>
      </LinkWrapper>

      {sublabel && <div className="side-nav-item--sublabel">{renderSublabel()}</div>}

      {children && (
        <SideBarSubMenu
          badge={badge}
          onClose={handleSideBarClose}
          icon={submenuIcon}
          label={renderSubmenuLabel() ?? undefined}
          labelWithoutTranslation={submenuLabelWithoutTranslation}
        >
          {children}
        </SideBarSubMenu>
      )}
    </li>
  );
};

export default SideNavItem;
